import { Cargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import HelpTooltip from '@/components/help/HelpTooltip';
import { Loader2, Sparkles, Save, AlertTriangle, Plus, Trash2, Pencil } from 'lucide-react';

const SOURCE_LABELS = {
  brand: 'Marca', own_usage: 'Ya usados por ti', ai_suggestion: 'Sugerencia IA', meta_public: 'Meta público',
};

function TagChip({ tag, count, onAdd, added }) {
  return (
    <button type="button" onClick={() => onAdd(tag)} disabled={added}
      className={`text-[11px] px-2 py-1 rounded-full border flex items-center gap-1 ${
        added ? 'border-[#F72585] bg-[#F72585]/10 text-[#F72585]' : 'border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8]'}`}>
      #{tag}{count != null && <span className="text-[#94A3B8]">({count})</span>}
      {!added && <Plus className="w-2.5 h-2.5" />}
    </button>
  );
}

/**
 * Fase 4 — cada fuente etiquetada explícitamente + refreshed_at. Nunca
 * muestra conteos de "Meta público" (no tenemos ese producto habilitado)
 * — se muestra como no disponible, con el motivo real.
 */
export default function HashtagAssistant({ topic, network, selected, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAi, setLoadingAi] = useState(false);
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [deletingGroupId, setDeletingGroupId] = useState(null);
  const [brandEditing, setBrandEditing] = useState(false);
  const [brandInput, setBrandInput] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await api.get('/tenant/social-studio/hashtags', { params: { network } });
      setData(data);
      const { data: g } = await api.get('/tenant/social-studio/hashtags/groups');
      setGroups(g.groups || []);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || 'No pudimos cargar las sugerencias de hashtags.';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [network]);

  useEffect(() => { load(); }, [load]);

  const suggestAi = async () => {
    if (!topic?.trim()) { toast.error('Escribe un tema para que la IA sugiera hashtags'); return; }
    setLoadingAi(true);
    try {
      const { data: r } = await api.get('/tenant/social-studio/hashtags', { params: { topic, network, use_ai: true } });
      setData((prev) => ({ ...prev, ai_suggestion: r.ai_suggestion }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoadingAi(false);
    }
  };

  const addTag = (tag) => {
    if (selected.includes(tag)) return;
    onChange([...selected, tag]);
  };
  const removeTag = (tag) => onChange(selected.filter((t) => t !== tag));

  const reloadGroups = async () => {
    const { data: g } = await api.get('/tenant/social-studio/hashtags/groups');
    setGroups(g.groups || []);
  };

  const saveGroup = async () => {
    if (!newGroupName.trim() || selected.length === 0) {
      toast.error('Ponle nombre al grupo y elige al menos un hashtag');
      return;
    }
    try {
      await api.post('/tenant/social-studio/hashtags/groups', { name: newGroupName.trim(), hashtags: selected });
      toast.success('Grupo guardado');
      setNewGroupName('');
      await reloadGroups();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo guardar el grupo');
    }
  };

  const deleteGroup = async (g) => {
    const ok = window.confirm(
      `¿Eliminar el grupo "${g.name}"?\n\nSolo se borra el grupo guardado. Los hashtags que ya agregaste a esta publicación se quedan.`
    );
    if (!ok) return;
    setDeletingGroupId(g.id);
    try {
      await api.delete(`/tenant/social-studio/hashtags/groups/${g.id}`);
      toast.success('Grupo eliminado');
      await reloadGroups();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo eliminar el grupo');
    } finally {
      setDeletingGroupId(null);
    }
  };

  const brandTags = data?.brand?.hashtags || [];

  const startEditBrand = () => {
    setBrandInput(brandTags.map((t) => `#${t}`).join(' '));
    setBrandEditing(true);
  };

  const saveBrandTags = async () => {
    const parsed = brandInput
      .split(/[\s,]+/)
      .map((t) => t.trim().replace(/^#+/, ''))
      .filter(Boolean);
    if (parsed.length === 0 && brandTags.length > 0) {
      if (!window.confirm('Vas a dejar tu marca sin hashtags fijos. ¿Continuar?')) return;
    }
    setSavingBrand(true);
    try {
      const { data: r } = await api.put('/tenant/social-studio/hashtags/brand', { hashtags: parsed });
      setData((prev) => ({ ...(prev || {}), brand: { ...(prev?.brand || {}), hashtags: r.hashtags || [] } }));
      setBrandEditing(false);
      toast.success('Hashtags de marca guardados');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron guardar los hashtags de marca');
    } finally {
      setSavingBrand(false);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-xs text-[#94A3B8] py-3"><Cargando tamaño={20} color="#94A3B8" /> Buscando hashtags…</div>;

  if (loadError) {
    return (
      <div className="border border-[#E2E8F0] rounded-lg p-3 space-y-2" data-testid="hashtag-assistant-error">
        <p className="text-xs text-red-600 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {loadError}
        </p>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load} data-testid="hashtag-assistant-retry">Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="hashtag-assistant">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((tag) => (
            <Badge key={tag} className="bg-[#F72585]/10 text-[#F72585] gap-1 cursor-pointer" onClick={() => removeTag(tag)}>
              #{tag} ✕
            </Badge>
          ))}
        </div>
      )}

      <div>
        <div className="flex items-center gap-1 mb-1">
          <p className="text-[10px] font-medium text-[#94A3B8]">Grupos guardados</p>
          <HelpTooltip text="Son paquetes de hashtags que guardaste con un nombre para reutilizarlos. Toca el nombre del grupo para agregar todos sus hashtags a esta publicación, o el bote de basura para borrar el grupo." />
        </div>
        {groups.length === 0 ? (
          <p className="text-[11px] text-[#94A3B8]">Sin grupos guardados todavía. Elige hashtags aquí abajo y guárdalos con un nombre para volver a usarlos.</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {groups.map((g) => (
              <span key={g.id} className="text-[11px] rounded-full border border-[#E2E8F0] text-[#64748B] flex items-center overflow-hidden">
                <button type="button" onClick={() => onChange([...new Set([...selected, ...g.hashtags])])}
                  title="Agregar los hashtags de este grupo"
                  data-testid={`hashtag-group-apply-${g.id}`}
                  className="px-2 py-1 hover:text-[#334155]">
                  {g.name} ({g.hashtags.length})
                </button>
                <button type="button" onClick={() => deleteGroup(g)} disabled={deletingGroupId === g.id}
                  title="Eliminar este grupo guardado"
                  aria-label={`Eliminar el grupo ${g.name}`}
                  data-testid={`hashtag-group-delete-${g.id}`}
                  className="px-1.5 py-1 border-l border-[#E2E8F0] text-[#94A3B8] hover:text-red-600 hover:bg-red-50 disabled:opacity-50">
                  {deletingGroupId === g.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Trash2 className="w-2.5 h-2.5" />}
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1">
            <p className="text-[10px] font-medium text-[#94A3B8]">{SOURCE_LABELS.brand}</p>
            <HelpTooltip text="Son los hashtags fijos de tu marca. Se guardan una sola vez y aparecen como sugerencia en todas tus publicaciones, para que tu marca se vea siempre igual." />
          </div>
          {!brandEditing && (
            <Button size="sm" variant="ghost" className="h-6 text-[10px] gap-1" onClick={startEditBrand} data-testid="brand-hashtags-edit">
              <Pencil className="w-3 h-3" /> {brandTags.length > 0 ? 'Editar' : 'Definir'}
            </Button>
          )}
        </div>

        {brandEditing ? (
          <div className="space-y-1.5">
            <Input value={brandInput} onChange={(e) => setBrandInput(e.target.value)}
              placeholder="Ejemplo: micafe cafedeespecialidad centrocdmx"
              className="h-8 text-xs" data-testid="brand-hashtags-input" />
            <p className="text-[10px] text-[#94A3B8]">
              Escríbelos separados por espacios o comas. El símbolo # es opcional y se quitan acentos y símbolos raros automáticamente. Deja el campo vacío para no usar hashtags de marca.
            </p>
            <div className="flex items-center gap-1.5">
              <Button size="sm" onClick={saveBrandTags} disabled={savingBrand} className="h-8 gap-1 text-xs" data-testid="brand-hashtags-save">
                {savingBrand ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar hashtags de marca
              </Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setBrandEditing(false)} disabled={savingBrand} data-testid="brand-hashtags-cancel">
                Cancelar
              </Button>
            </div>
          </div>
        ) : brandTags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {brandTags.map((t) => <TagChip key={t} tag={t} onAdd={addTag} added={selected.includes(t)} />)}
          </div>
        ) : (
          <p className="text-[11px] text-[#94A3B8]">Aún no defines hashtags de marca. Defínelos una vez y te los sugerimos en cada publicación.</p>
        )}
      </div>

      {data?.own_usage?.hashtags?.length > 0 && (
        <div>
          <p className="text-[10px] font-medium text-[#94A3B8] mb-1">{SOURCE_LABELS.own_usage}</p>
          <div className="flex flex-wrap gap-1">
            {data.own_usage.hashtags.map((h) => (
              <TagChip key={h.hashtag} tag={h.hashtag} count={h.times_used} onAdd={addTag} added={selected.includes(h.hashtag)} />
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] font-medium text-[#94A3B8]">{SOURCE_LABELS.ai_suggestion}</p>
          <Button size="sm" variant="ghost" className="h-6 text-[10px] gap-1" onClick={suggestAi} disabled={loadingAi}>
            {loadingAi ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Sugerir
          </Button>
        </div>
        {data?.ai_suggestion?.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {data.ai_suggestion.hashtags.map((t) => <TagChip key={t} tag={t} onAdd={addTag} added={selected.includes(t)} />)}
          </div>
        )}
        {data?.ai_suggestion?.blocked && (
          <p className="text-[10px] text-amber-600">{data.ai_suggestion.block_reason}</p>
        )}
      </div>

      <div className="border border-dashed border-[#E2E8F0] rounded-lg p-2 text-[11px] text-[#94A3B8] flex items-start gap-1.5">
        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>{data?.meta_public?.reason}</span>
      </div>

      <div className="flex items-center gap-1.5">
        <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="Nombre del grupo a guardar" className="h-8 text-xs" data-testid="hashtag-group-name" />
        <Button size="sm" variant="outline" onClick={saveGroup} className="gap-1 h-8" title="Guardar los hashtags elegidos como un grupo reutilizable" data-testid="hashtag-group-save"><Save className="w-3.5 h-3.5" /></Button>
        <HelpTooltip text="Guarda los hashtags que elegiste arriba como un grupo con nombre, para volver a usarlos de un solo clic en otra publicación." />
      </div>
    </div>
  );
}
